import React from 'react';
import { styled } from '@glitz/react';
import { H3, Paragraph, ResponsiveSize, TextBlockSmall } from 'Shared/SharedComponents/atoms/Typography';
import FillableCircles from 'Shared/SharedComponents/molecules/FillableCircles';
import ReviewViewModelLabels from 'Product/VariationViewModel+ReviewViewModelLabels.type';
import { Plus12x12 } from 'Shared/Icons/Plus';
import { Minus12x12 } from 'Shared/Icons/Minus';
import * as style from 'Shared/Style';

type AttributeRating = {
  attributeName: string;
  rating: number;
};

type ReviewConsumer = {
  displayName: string;
};

export type Review = {
  createdAt: string;
  stars: number;
  content: string;
  consumer: ReviewConsumer;
  attributeRatings: AttributeRating[];
}

type PropType = {
  review: Review;
  reviewLabels: ReviewViewModelLabels;
  isCompact: boolean;
};

const Wrap = styled.div({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  gap: '16px',
  borderRadius: '8px',
  backgroundColor: style.colors.monochrome.extraLightGrey,
  padding: { x: '12px', top: '12px', bottom: '24px' },
  ['@media ' + style.mediaMinLarge]: {
    padding: { x: '16px', top: '16px', bottom: '24px' },
  },
})


const Author = styled.div({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
})

const Answer = styled.div({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: '6px'
})

const P = styled(Paragraph, {
  display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '8px'
})

const Minus = styled(Minus12x12, {
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'inherit',
})

const Plus = styled(Plus12x12, {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'inherit',
})

const ReviewTime = styled(TextBlockSmall, {
  color: style.colors.monochrome.a11ygrey,
  textTransform: 'uppercase',
});

const TopContent = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: '4px'
})

function ReviewCard({ review, reviewLabels, isCompact }: PropType) {

  // function sizeFitLabel(fitOption: string) {
  //   switch (fitOption) {
  //     case 'small':
  //       return reviewLabels.fitSmall;
  //     case 'normal':
  //       return reviewLabels.fitNormal;
  //     case 'large':
  //       return reviewLabels.fitBig;
  //     default:
  //       return reviewLabels.fitNormal;
  //   }
  // }

  function dateDiff(inputDate: string) {
    if (inputDate === null || inputDate === undefined) {
      return '';
    }

    const _MS_PER_DAY = 1000 * 60 * 60 * 24;
    // Discard the time and time-zone information.
    let b = new Date();
    let a = new Date(inputDate);
    const utc1 = Date.UTC(a.getFullYear(), a.getMonth(), a.getDate());
    const utc2 = Date.UTC(b.getFullYear(), b.getMonth(), b.getDate());
    let result = Math.floor((utc2 - utc1) / _MS_PER_DAY);

    if (result > 31) {
      result = Math.floor(result / 31);

      if (result > 12) {
        const year = Math.floor(result / 12);
        return `${year} ${year > 1 ? reviewLabels.years : reviewLabels.year} ${reviewLabels.ago}`;
      }
      if (result === 0) {
        result = 1;
      }

      return `${result} ${result > 1 ? reviewLabels.months : reviewLabels.month} ${reviewLabels.ago}`;
    }

    return `${result} ${result > 1 ? reviewLabels.days : reviewLabels.day} ${reviewLabels.ago}`;
  }

  return (
    <Wrap>
      <TopContent>
        <Author className="review-card__author-review">
          <H3>{review.consumer.displayName}</H3>
          <FillableCircles numberToFill={review.stars} />
        </Author>
        <ReviewTime fontSize={ResponsiveSize.D12_M12} upperCase>{dateDiff(review.createdAt)}</ReviewTime>
      </TopContent>
      <div className="review-card__question-answers">
        {review.attributeRatings.map((attribute, index) => {
          return (
            <Answer key={`qa_size-${index}`}>
              <TextBlockSmall upperCase>{attribute.attributeName}:</TextBlockSmall>
              <TextBlockSmall fontSize={ResponsiveSize.D16_M16}>{attribute.rating}</TextBlockSmall>
            </Answer>
          );
        })}
      </div>
      <Paragraph fontSize={ResponsiveSize.D16_M16}>{review.content}</Paragraph>
    </Wrap>
  );
}

export default ReviewCard;
